import { useQuery } from '@tanstack/react-query';
import { type MessageKey, t } from '../../i18n';
import { api } from '../../lib/api';

// Datos de /api/plataforma (Administrador de Plataforma, guía 4.3).

export type OrgKind = 'linea' | 'pais' | 'regional';

export interface OrganizationFields {
  name: string;
  alias: string | null;
  kind: OrgKind;
  groupName: string;
  timezone: string;
  /** País ISO de dos letras: de él salen los feriados del SLA. */
  countryCode: string | null;
}

export interface PlatformOrganization extends OrganizationFields {
  id: string;
  departments: number;
  users: number;
  /** El actor la ve por sus roles. */
  inSession: boolean;
}

export interface Department {
  id: string;
  name: string;
  users: number;
}

export interface TenantSummary {
  id: string;
  name: string;
  subdomain: string;
  active: boolean;
  isPlatform: boolean;
  storageQuotaBytes: number;
  createdAt: string;
  organizations: number;
  users: number;
}

export const KEYS = {
  organizaciones: ['plataforma', 'organizaciones'] as const,
  departamentos: (orgId: string) => ['plataforma', 'departamentos', orgId] as const,
  tenants: ['plataforma', 'tenants'] as const,
};

export const useOrganizations = () =>
  useQuery({ queryKey: KEYS.organizaciones, queryFn: () => api<PlatformOrganization[]>('/api/plataforma/organizaciones') });

export const ORG_KINDS: Array<{ value: OrgKind; label: MessageKey }> = [
  { value: 'linea', label: 'plataforma.tipo_linea' },
  { value: 'pais', label: 'plataforma.tipo_pais' },
  { value: 'regional', label: 'plataforma.tipo_regional' },
];

export const kindLabel = (kind: string) => t(ORG_KINDS.find((k) => k.value === kind)?.label ?? 'plataforma.tipo_linea');

/** Países de las organizaciones (ISO 3166-1). Si una organización usa otro, se muestra igual. */
export const COUNTRIES: ReadonlyArray<{ code: string; name: string }> = [
  { code: 'PA', name: 'Panamá' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'CO', name: 'Colombia' },
  { code: 'DO', name: 'República Dominicana' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'ES', name: 'España' },
  { code: 'US', name: 'Estados Unidos' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'HN', name: 'Honduras' },
  { code: 'MX', name: 'México' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'VE', name: 'Venezuela' },
];

/** Zonas horarias IANA del navegador, para sugerir (la API valida). */
export function timeZones(): string[] {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return ['America/Panama', 'America/Costa_Rica', 'America/Guatemala', 'America/El_Salvador', 'America/Guayaquil', 'America/Caracas', 'America/Santo_Domingo', 'America/New_York'];
  }
}

/** Dirección de un tenant: el subdominio reemplaza al de la dirección actual (sit365.gruposiuma.com → demo.gruposiuma.com). */
export function tenantUrl(subdomain: string): string {
  const { protocol, host } = window.location;
  const rest = host.split('.').slice(1).join('.');
  return `${protocol}//${subdomain}.${rest || host}`;
}

/** Sugiere un subdominio a partir del nombre: "Cliente Demo" → "cliente-demo". */
export function suggestSubdomain(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 30)
    .replace(/-+$/, '');
}
